import { VehicleFuelType } from '@api/models/types';
import { getAccount, isLATAMTenant } from '@common/login/selectors';
import {
    getAdvanceVehicles,
    getFeatureToggles,
    getPerformReducedBus,
    getPerformVehicles,
} from '@common/permissions/selectors';
import { Loadable, LoadableType } from '@data/loadable';
import { getVehicles } from '@data/selectors';
import { hideSidebar, SidebarData, sidebarData } from '@features/sidebar';
import { setSelectedFuelTypeGroups } from '@features/tree/actions';
import { getSelectedFuelTypes } from '@features/tree/selectors';
import TreeFilterButtonsV2 from '@features/tree/TreeFilterButtonsV2';
import { getTableViewType, isMobile } from '@features/ui/reducer';
import { isFilteringOnSegment } from '@features/ui/selectors';
import { hydrateColumns } from '@utils/columns';
import { calculateColumnOrder } from '@utils/sorting';
import { findValueExtractor, sortByProperty, SortDirection } from '@utils/sortyByProperty';
import _ from 'lodash';
import always from 'lodash/fp/always';
import identity from 'lodash/fp/identity';
import moment from 'moment';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { compose, Dispatch } from 'redux';
import { Column } from 'src/columns/createColumn';
import { State } from 'src/setup/types';
import {
    Attribute,
    DateRange,
    HydratedEntity,
    HydratedEntityWithChildren,
    Id,
    Map,
    Vehicle,
} from 'src/types';

import { loadingFormatter } from '../../columns/DOM';
import SimpleErrorState from '../../components/SimpleErrorState';
import SettingsDialog from '../../components/table/SettingsDialog';
import SettingsToolbar from '../../components/table/SettingsToolbar';
import Table, { SortBy } from '../../components/table/Table';
import UseCases from '../../components/UseCases';
import { VEHICLE_ROUTE } from '../../constants/routes';
import AdministrationSettingsFooter from '../../features/settings/AdministrationSettingsFooter';
import Toolbar from '../../features/ui/Toolbar';
import {
    closeRow,
    openRow,
    setColumnOrderForUseCase,
    setFilteredColumnNamesForUseCase,
    setSortedBy,
    setUseCase,
} from './actions';
import MixedFleetAlert from './MixedFleetAlert';
import { DEFAULT_USE_CASE_CONFIG, UseCaseSetting } from './reducer';
import {
    getOpenRows,
    getSelectedUseCase,
    getSelectedUseCaseConfig,
    getSortedBy,
    getUseCaseConfig,
    getUseCaseSetting,
} from './selectors';
import { defaultUseCaseConfig, UseCaseConfig } from './useCaseConfig';
import { decorateWithAdditionalInformation } from './utils';
import DownloadMenu from './VehicleDownloadMenu';
import VehicleFilterSummary from './VehicleFilterSummary';
import VehicleState from './VehicleState';
import VehicleSummary from './VehicleSummary';
const sortChildrenBy =
    (sortFn: (children: HydratedEntity[]) => unknown) => (data: HydratedEntityWithChildren[]) =>
        data.map((d: HydratedEntityWithChildren) => ({
            ...d,
            children: sortFn(d.children),
        }));
const hasLoadableChildren = (loadable: LoadableType<HydratedEntityWithChildren[]>) =>
    Loadable.map(loadable, (data) => Boolean(data.length));
const alwaysTrue = always(true);
const alwaysFalse = always(false);
const getShouldShowGraph = compose(
    (loadable) => Loadable.cata(loadable, identity, alwaysTrue, alwaysTrue, alwaysTrue),
    hasLoadableChildren,
);
const getAreSettingsEnabled = compose(
    (loadable) => Loadable.cata(loadable, identity, alwaysFalse, alwaysFalse, alwaysFalse),
    hasLoadableChildren,
);
const createSortAndFlattenFunction = (
    openRows: string[],
    sortColumn: Column | undefined,
    sortOrder: SortDirection,
) => {
    const { key, dataField } = sortColumn || {};
    const dataSort = compose(
        sortChildrenBy((children: HydratedEntity[]) =>
            sortByProperty(children, dataField, sortOrder, findValueExtractor(key)),
        ),
        sortChildrenBy((children: HydratedEntity[]) =>
            sortByProperty(
                children,
                'drivers',
                SortDirection.ASCENDING,
                findValueExtractor('drivers'),
            ),
        ),
        (data: HydratedEntityWithChildren[]) =>
            sortByProperty(data, dataField, sortOrder, findValueExtractor(key)),
    );
    const flattenChildren = (data: HydratedEntityWithChildren[]) =>
        data.reduce((result: HydratedEntityWithChildren[], row: HydratedEntityWithChildren) => {
            const isRowOpen = openRows.includes(row.id);
            return [
                ...result,
                row,
                ...((isRowOpen && row.children) || []),
            ] as HydratedEntityWithChildren[];
        }, []);
    return compose(flattenChildren, dataSort);
};
const createTransformFunction =
    (
        advanceVehicleIds: Id[],
        performVehicleIds: Id[],
        getPerformReducedBus: Id[],
        shouldShowFuelType: boolean,
    ) =>
    (entities: HydratedEntityWithChildren[]) =>
        entities.map((entity: HydratedEntityWithChildren) =>
            decorateWithAdditionalInformation({
                entity,
                advanceVehicleIds,
                performVehicleIds,
                performReducedBus: getPerformReducedBus,
                shouldShowFuelType,
            }),
        );
const openExpanderRow =
    ({
        openRows,
        openRow,
        closeRow,
    }: {
        openRows: Id[];
        openRow: (id: Id) => void;
        closeRow: (id: Id) => void;
    }) =>
    (row: HydratedEntityWithChildren) => {
        const id = row.id;
        if (openRows.includes(id)) {
            closeRow(id);
        } else {
            openRow(id);
        }
    };
const findColumnByKey = (columns: Column[], key: string | undefined) =>
    columns.find((column) => column.key === key);
const transformColumnStringsToObjects = (
    columnOrder: string[],
    filteredColumnNames: (string | undefined)[],
    summaryColumns: Column[],
    columns: Column[],
) => {
    const columnKeys = _.map(columns, 'key');
    const columnKeysInRightOrder = calculateColumnOrder(columnKeys, columnOrder);
    const columnsInRightOrder = columnKeysInRightOrder.map((key) => findColumnByKey(columns, key));
    let filteredColumns = columnsInRightOrder
        .filter(Boolean)
        .filter((column) => !filteredColumnNames.includes(column?.key));
    return {
        columnKeysInRightOrder,
        filteredColumns,
        filteredSummaryColumns: summaryColumns.filter(
            (column) => !filteredColumnNames.includes(column.key),
        ),
    };
};
type Props = {
    dateRange?: DateRange;
    onItemClick: (data: SidebarData) => void;
    setUseCase: (useCaseKey: string) => void;
    useCase?: UseCaseConfig | undefined;
    selectedVehicle?: Id;
    vehicles?: Id[];
    parentsWithChildren: LoadableType<HydratedEntityWithChildren[]>;
    useCaseConfig?: UseCaseConfig[];
    setFilteredColumnNamesForUseCase: ({
        useCaseKey,
        filteredColumnNames,
    }: {
        useCaseKey: string;
        filteredColumnNames: string[];
    }) => void;
    setColumnOrderForUseCase: ({
        useCaseKey,
        columnOrder,
    }: {
        useCaseKey: string;
        columnOrder: string[];
    }) => void;
    hideSidebar: () => void;
    useCaseSettings?: UseCaseSetting;
    sortBy?: SortBy;
    setSortBy: (sortBy: SortBy) => void;
    performVehicles?: Id[];
    advanceVehicles?: Id[];
    performReducedBus?: Id[];
    openRow: (id: Id) => void;
    closeRow: (id: Id) => void;
    isFilteringOnSegment: boolean;
    openRows?: Id[];
    viewType: string;
    isMobile?: boolean;
    account?: string;
    isLatamTenant?: boolean;
    allVehicles?: Map<Vehicle>;
    selectedFuelTypes: VehicleFuelType[];
    selectVehiclesByFuelType: (arg: VehicleFuelType[]) => void;
    treeFilter: boolean;
    attributes: LoadableType<Attribute[]>;
};
export const Content = (props: Props) => {
    const {
        dateRange = {
            start: moment().subtract(1, 'week').toDate(),
            end: moment().toDate(),
        },
        onItemClick,
        setUseCase,
        useCase = { columns: [], summaryColumns: [], key: '' },
        selectedVehicle,
        vehicles = [],
        parentsWithChildren,
        useCaseConfig = defaultUseCaseConfig,
        setFilteredColumnNamesForUseCase,
        setColumnOrderForUseCase,
        hideSidebar,
        useCaseSettings = DEFAULT_USE_CASE_CONFIG,
        sortBy = { key: 'vehicles', order: SortDirection.ASCENDING },
        setSortBy,
        performVehicles = [],
        advanceVehicles = [],
        performReducedBus = [],
        allVehicles = {},
        openRow,
        closeRow,
        isFilteringOnSegment,
        openRows = [],
        viewType,
        isMobile = false,
        account = '',
        isLatamTenant = false,
        selectedFuelTypes = [],
        selectVehiclesByFuelType,
        treeFilter,
        attributes,
    } = props;
    const {
        columns: rawColumns,
        summaryColumns: rawSummaryColumns,
        key: selectedUseCaseKey,
        defaultHiddenColumns,
    } = useCase;
    useEffect(() => {
        const viewNames: Record<string, string> = {
            overview: 'Overview',
            consumption: 'Consumption',
            'operating-condition': 'Operating Status',
        };
        const viewName = viewNames[selectedUseCaseKey];
        if (!viewName) return;
        const trackingWindow = window as Window & {
            dataLayer?: Array<Record<string, string>>;
        };
        (trackingWindow.dataLayer ??= []).push({
            event: 'view_change',
            view_name: viewName,
            section_name: 'Performance Analysis',
        });
    }, [selectedUseCaseKey]);
    const loadedAttributes = useMemo(
        () =>
            Loadable.cata(
                attributes,
                (d) => d,
                () => [],
                () => [],
                () => [],
            ),
        [attributes],
    );
    const columns = useMemo(
        () => hydrateColumns(rawColumns, loadedAttributes),
        [rawColumns, loadedAttributes],
    );
    const summaryColumns = useMemo(
        () => hydrateColumns(rawSummaryColumns, loadedAttributes, { plain: true }),
        [rawSummaryColumns, loadedAttributes],
    );
    const [areSettingsVisible, toggleSettings] = useState(false);
    const { columnOrder, filteredColumnNames } = useCaseSettings;
    let { columnKeysInRightOrder, filteredColumns, filteredSummaryColumns } = useMemo(
        () =>
            transformColumnStringsToObjects(
                columnOrder,
                filteredColumnNames,
                summaryColumns,
                columns,
            ),
        [columnOrder, columns, filteredColumnNames, summaryColumns],
    );
    const selectedVehicles = vehicles.map((id) => allVehicles[id]).filter(Boolean);
    const categorizeVehicles = (vehicles?: Id[], allVehicles?: Map<Vehicle>) => {
        const selectedDieselVehicles: Id[] = [];
        const selectedElectricVehicles: Id[] = [];
        vehicles?.forEach((id) => {
            const selectedVehicle = allVehicles?.[id];
            if (selectedVehicle) {
                (selectedVehicle.fuelType === VehicleFuelType.ELECTRIC
                    ? selectedElectricVehicles
                    : selectedDieselVehicles
                ).push(selectedVehicle.id);
            }
        });
        return { selectedDieselVehicles, selectedElectricVehicles };
    };
    const { selectedDieselVehicles, selectedElectricVehicles } = categorizeVehicles(
        vehicles,
        allVehicles,
    );
    const containsElectricVehicle = selectedVehicles.some(
        (selectedVehicle) => selectedVehicle.fuelType === VehicleFuelType.ELECTRIC,
    );
    const shouldHideCo2Emission = selectedFuelTypes.length === 1 && containsElectricVehicle;
    if (shouldHideCo2Emission) {
        filteredColumns = filteredColumns.filter((column) => column?.key !== 'co2Emission');
        filteredSummaryColumns = filteredSummaryColumns.filter(
            (column) => column?.key !== 'co2Emission',
        );
    }
    const setColumnOrder = (order: string[]) =>
        setColumnOrderForUseCase({ useCaseKey: selectedUseCaseKey, columnOrder: order });
    const setColumns = (columnNames: string[]) =>
        setFilteredColumnNamesForUseCase({
            useCaseKey: selectedUseCaseKey,
            filteredColumnNames: columnNames,
        });
    const performAndAdvanceVehicles = performVehicles
        .concat(advanceVehicles)
        .concat(performReducedBus);
    const upsellVehicleIds = _.difference(vehicles, performAndAdvanceVehicles);
    const upsellVehicles = upsellVehicleIds
        .map((vehicleId: string) => allVehicles[vehicleId])
        .filter(Boolean)
        .sort((a, b) => a.name.localeCompare(b.name));
    const expandedChildren: LoadableType<HydratedEntityWithChildren[]> = useMemo(() => {
        const sortAndFlatten = createSortAndFlattenFunction(
            openRows,
            findColumnByKey(columns, sortBy.key),
            sortBy.order,
        );
        const transform = createTransformFunction(
            advanceVehicles,
            performVehicles,
            performReducedBus,
            containsElectricVehicle,
        );
        return Loadable.map(
            parentsWithChildren,
            compose<HydratedEntityWithChildren[]>(sortAndFlatten, transform),
        );
    }, [
        openRows,
        columns,
        sortBy.key,
        sortBy.order,
        advanceVehicles,
        performVehicles,
        performReducedBus,
        containsElectricVehicle,
        parentsWithChildren,
    ]);
    const shouldShowGraph =
        isFilteringOnSegment || (getShouldShowGraph(expandedChildren) && Boolean(vehicles.length));
    const areSettingsEnabled = getAreSettingsEnabled(expandedChildren);
    const onOpenChildren = useCallback(openExpanderRow({ openRows, openRow, closeRow }), [
        openRows,
        openRow,
        closeRow,
    ]);
    const noVehiclesSelected = (
        <div className="margin-top-20pct">
            <VehicleState
                headlineId="vehicleAnalysis.noVehiclesSelected"
                messageId="vehicleAnalysis.noVehiclesSelected.explanation"
            />
        </div>
    );
    const handleRowToggle = useCallback(
        (
            {
                id,
                level,
                children,
            }: { id?: string; level?: number; children?: HydratedEntity[] } = {},
            active: boolean,
        ) => {
            if (!active && id) {
                onItemClick({
                    id: id!,
                    level: level!,
                    childrenIds: (children || []).map((child: HydratedEntity) => child.id),
                    attributes: loadedAttributes,
                });
            } else {
                hideSidebar();
            }
        },
        [hideSidebar, onItemClick, loadedAttributes],
    );
    const table = Loadable.cata(
        expandedChildren,
        (data) => {
            if (!data.length && !upsellVehicles.length) {
                return (
                    <div className={!isFilteringOnSegment ? 'margin-top-20pct' : ''}>
                        <VehicleState
                            headlineId="vehicleAnalysisNoVehiclesReturned"
                            messageId="vehicleAnalysisNoVehiclesReturnedExplanation"
                        />
                    </div>
                );
            }
            return (
                <Table
                    key={_.get(useCase, 'key')}
                    performAndAdvanceVehicles={performAndAdvanceVehicles}
                    filteredColumns={filteredColumns.filter((c): c is Column => !!c)}
                    onOpenChildren={onOpenChildren}
                    sortBy={sortBy}
                    onSort={setSortBy}
                    selectedElements={selectedVehicle ? [selectedVehicle] : []}
                    openRows={openRows}
                    upsellVehicles={upsellVehicles}
                    data={data}
                    onRowClicked={handleRowToggle}
                    viewType={viewType}
                    location="fleetAnalysis"
                    shouldShowFuelType={containsElectricVehicle}
                />
            );
        },
        () => (
            <div className="margin-top-20">
                <SimpleErrorState headlineId="error.default" messageId="error.server" />
            </div>
        ),
        () => (
            <Table
                key={'loading'}
                filteredColumns={filteredColumns
                    .filter((c): c is Column => !!c)
                    .map((c) => ({ ...c, formatter: loadingFormatter }))}
                onOpenChildren={() => {}}
                sortBy={sortBy}
                onSort={setSortBy}
                selectedElements={[]}
                openRows={[]}
                data={_.fill(Array(3), { level: 1, id: '', isLoading: true })}
                onRowClicked={() => {}}
                viewType={viewType}
                shouldShowFuelType={false}
            />
        ),
        () => noVehiclesSelected,
    );
    const filterSummary = useMemo(
        () => <VehicleFilterSummary loadableEntities={parentsWithChildren} dateRange={dateRange} />,
        [dateRange, parentsWithChildren],
    );
    const filterVehicles = (selectedVehicles: Id[], performVehicles: Id[]) =>
        performVehicles ? _.intersection(selectedVehicles, performVehicles) : performVehicles;
    const selectedPerformVehicles = performVehicles
        ? _.intersection(vehicles, performVehicles)
        : vehicles;
    const selectedPerformDieselVehicles = filterVehicles(selectedDieselVehicles, performVehicles);
    const selectedPerformElectricVehicles = filterVehicles(
        selectedElectricVehicles,
        performVehicles,
    );
    return (
        <div data-test="VehicleContent">
            <MixedFleetAlert />
            <Toolbar onDateRangeChange={hideSidebar}>
                {treeFilter && (
                    <TreeFilterButtonsV2
                        selectedFuelTypes={selectedFuelTypes}
                        onSelectionChange={selectVehiclesByFuelType}
                    />
                )}
                <div className="table-toolbar-column table-toolbar-column-spacer">
                    <UseCases
                        onSelect={setUseCase}
                        active={selectedUseCaseKey}
                        useCaseConfig={useCaseConfig}
                        location="fleetAnalysis"
                    />
                </div>
                <div className="table-toolbar-column">
                    <DownloadMenu
                        dateRange={dateRange}
                        vehicles={isLatamTenant ? vehicles : selectedPerformVehicles}
                        dieselVehicles={
                            isLatamTenant ? selectedDieselVehicles : selectedPerformDieselVehicles
                        }
                        electricVehicles={
                            isLatamTenant
                                ? selectedElectricVehicles
                                : selectedPerformElectricVehicles
                        }
                        disabled={!areSettingsEnabled}
                        useCaseKey={useCase.key}
                        isLatamTenant={isLatamTenant}
                    />
                </div>
            </Toolbar>
            <SettingsDialog
                areSettingsVisible={areSettingsVisible}
                hiddenColumns={filteredColumnNames}
                toggleSettings={toggleSettings}
                defaultHiddenColumns={defaultHiddenColumns}
                columnOrder={columnKeysInRightOrder}
                setColumnOrder={setColumnOrder}
                defaultColumnOrder={columns.map((column) => column.key)}
                setColumns={setColumns}
                columns={columns}
                account={account}
                location="fleetAnalysis"
                useCase={`Fleet${_.upperFirst(_.camelCase(selectedUseCaseKey))}`}
            />
            <div
                data-test={`fleet-view-panel-${
                    selectedUseCaseKey === 'operating-condition'
                        ? 'operating-status'
                        : selectedUseCaseKey
                }`}
            >
                {!isMobile && shouldShowGraph && (
                    <VehicleSummary
                        dateRange={dateRange}
                        columns={filteredSummaryColumns}
                        filterSummary={filterSummary}
                        attributes={attributes}
                    />
                )}
                {vehicles.length ? (
                    <Fragment>
                        <SettingsToolbar
                            disabled={!areSettingsEnabled}
                            toggleSettings={toggleSettings}
                            location="fleetAnalysis"
                        />
                        {table}
                    </Fragment>
                ) : (
                    noVehiclesSelected
                )}
            </div>
            <AdministrationSettingsFooter />
        </div>
    );
};
const mapDispatchToProps = (dispatch: Dispatch) => ({
    setFilteredColumnNamesForUseCase: ({
        useCaseKey,
        filteredColumnNames,
    }: {
        useCaseKey: string;
        filteredColumnNames: string[];
    }) => dispatch(setFilteredColumnNamesForUseCase({ useCaseKey, filteredColumnNames })),
    setColumnOrderForUseCase: ({
        useCaseKey,
        columnOrder,
    }: {
        useCaseKey: string;
        columnOrder: string[];
    }) => dispatch(setColumnOrderForUseCase({ useCaseKey, columnOrder })),
    setUseCase: (useCaseKey: string) => dispatch(setUseCase(useCaseKey)),
    hideSidebar: () => dispatch(hideSidebar()),
    setSortBy: (sortBy: SortBy) => dispatch(setSortedBy(sortBy)),
    openRow: (id: Id) => dispatch(openRow({ id })),
    closeRow: (id: Id) => dispatch(closeRow({ id })),
    selectVehiclesByFuelType: (selectedFuelTypes: VehicleFuelType[]) =>
        dispatch(setSelectedFuelTypeGroups(selectedFuelTypes)),
});
export default connect(
    (state: State) => ({
        useCaseSettings: getUseCaseSetting(state, getSelectedUseCase(state)),
        useCaseConfig: getUseCaseConfig(),
        useCase: getSelectedUseCaseConfig(state),
        sortBy: getSortedBy(state),
        selectedVehicle: _.get(sidebarData(state, VEHICLE_ROUTE), 'id'),
        performVehicles: getPerformVehicles(state),
        advanceVehicles: getAdvanceVehicles(state),
        performReducedBus: getPerformReducedBus(state),
        isFilteringOnSegment: isFilteringOnSegment(state),
        openRows: getOpenRows(state),
        viewType: getTableViewType(state),
        isMobile: isMobile(state),
        account: getAccount(state),
        isLatamTenant: isLATAMTenant(state),
        allVehicles: getVehicles(state),
        selectedFuelTypes: getSelectedFuelTypes(state),
        treeFilter: getFeatureToggles(state).treeFilter as boolean,
    }),
    mapDispatchToProps,
)(Content);
